import { useEffect, useMemo, useState } from "react";
import { useWebClientApi } from "src/api";
import {
  fromBorrowerConsent,
  toConsentUpdateReq,
  type ConsentFormData,
} from "src/api/adapters";
import {
  useResponseRedirect,
  useRoleForBorrower,
  useSectionProgress,
} from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import type { DtoBorrower } from "@porchpass/api-client";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import {
  DisclaimerWithAcknowledgement,
  TermsAgreement,
} from "./ConsentDisclaimer";
import {
  FormTable,
  FormTableBody,
  PiiFields,
  queueScrollToFirstFormFieldError,
  Spinner,
  UserAccountIcon,
} from "@porchpass/ui";
import {
  buildConsentSubmitData,
  consentPiiErrorsAfterSubmit,
  getConsentSsnDigits,
  getConsentSsnFieldError,
  fromConsentPiiFormData,
  toConsentPiiFormData,
} from "src/forms/adapters/pii";
import {
  notEmpty,
  test,
  validate,
  type ValidationMapperFunc,
  isValidDate,
  safeNumber,
  isAgeValid,
  isPastOrCurrentDate,
} from "src/utils";

export default () => {
  const api = useWebClientApi();
  const role = useRoleForBorrower();
  const sectionProgress = useSectionProgress();
  const {
    formData,
    updateField,
    loading: formDataLoading,
  } = useFormLoad<ConsentFormData>(
    {},
    () => api.getBorrower(),
    (data) => fromBorrowerConsent(data as DtoBorrower),
  );
  const [
    submitHandler,
    loading,
    response,
    hasAttemptedFormSubmission,
    apiError,
  ] = useDlaFormSubmit((data: ConsentFormData) =>
    api.updateConsent(toConsentUpdateReq(data)),
  );
  const nextPagePath =
    (sectionProgress["consent"] && sectionProgress["sales_feedback"]) ||
    role === "COBORROWER"
      ? "/../../progress"
      : "/final-steps/sales-person";
  useResponseRedirect(nextPagePath, response);

  const validationMapper: ValidationMapperFunc<ConsentFormData> = (
    formData,
  ) => ({
    ssn_start: [
      test(
        () => getConsentSsnDigits(formData).length === 9,
        "Must have 9 numbers",
      ),
      test(
        () => getConsentSsnDigits(formData) !== "000000000",
        "Cannot be all zeroes",
      ),
    ],
    ssn_middle: [],
    ssn_end: [],
    dob_month: [test(notEmpty, "Must enter date of birth")],
    dob_day: [],
    dob_year: [
      test(notEmpty, "Must enter date of birth"),
      test(
        () =>
          isValidDate(
            safeNumber(formData.dob_month),
            safeNumber(formData.dob_day),
            safeNumber(formData.dob_year),
          ),
        "Please enter a valid date",
      ),
      test(
        () =>
          isPastOrCurrentDate(
            formData.dob_month ?? "",
            formData.dob_year ?? "",
            formData.dob_day ?? "",
          ),
        "DOB must be in the past",
      ),
      test(
        () =>
          isAgeValid(
            safeNumber(formData.dob_month),
            safeNumber(formData.dob_day),
            safeNumber(formData.dob_year),
          ),
        "Must be at least 18 years old",
      ),
    ],
    is_acknowledged: [test((val) => Boolean(val), "Must be acknowledged")],
    disclaimer: [test(notEmpty, "Must be signed")],
  });

  const piiValue = useMemo(() => toConsentPiiFormData(formData), [formData]);

  const submitData = useMemo(
    () => buildConsentSubmitData(formData, piiValue),
    [formData, piiValue],
  );

  const formErrors = validate<ConsentFormData>(submitData, validationMapper);

  const [ssnApiErrorDismissed, setSsnApiErrorDismissed] = useState(false);

  const ssnApiFieldError = useMemo(() => {
    if (!hasAttemptedFormSubmission || ssnApiErrorDismissed) {
      return undefined;
    }

    return getConsentSsnFieldError(apiError);
  }, [apiError, hasAttemptedFormSubmission, ssnApiErrorDismissed]);

  const piiErrors = useMemo(() => {
    const base = consentPiiErrorsAfterSubmit(
      formErrors,
      hasAttemptedFormSubmission,
    );

    return {
      ...base,
      ssn: base.ssn || ssnApiFieldError,
    };
  }, [formErrors, hasAttemptedFormSubmission, ssnApiFieldError]);

  useEffect(() => {
    if (!ssnApiFieldError) {
      return;
    }

    queueScrollToFirstFormFieldError();
  }, [ssnApiFieldError]);

  useEffect(() => {
    setSsnApiErrorDismissed(false);
  }, [apiError]);

  const applyPiiPatch = (patch: Partial<typeof piiValue>) => {
    if (patch.ssn !== undefined) {
      setSsnApiErrorDismissed(true);
    }

    const mapped = fromConsentPiiFormData(patch);
    for (const [key, val] of Object.entries(mapped)) {
      if (val !== undefined) {
        updateField(key as keyof ConsentFormData)(val);
      }
    }
  };

  const handleSubmit = () => {
    const nextSubmitData = buildConsentSubmitData(formData, piiValue);
    const submitErrors = validate<ConsentFormData>(
      nextSubmitData,
      validationMapper,
    );
    void submitHandler(nextSubmitData, submitErrors);
  };

  return (
    <FormLayout
      title="Almost done!"
      subtitle="Please enter the information below and read our terms. Once you sign, if edits need to be made, please contact your sales rep."
      headerTitle="SSN and Consent"
      stepIndex={11}
      footerTitle={sectionProgress["consent"] ? "Save" : "Next"}
      clickHandler={handleSubmit}
      isLoading={loading}
    >
      <Col>
        {formDataLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <Spinner variant="blue" />
          </div>
        ) : (
          <>
            <FormTable
              title="Personal Information"
              headerIcon={
                <UserAccountIcon
                  variant="bulk-rounded"
                  size={16}
                  className="text-[#6F41E6]"
                />
              }
            >
              <FormTableBody>
                <PiiFields
                  value={piiValue}
                  showBankruptcySection={false}
                  errors={piiErrors}
                  onChange={applyPiiPatch}
                />
              </FormTableBody>
            </FormTable>

            <TermsAgreement
              clickHandler={() =>
                updateField("is_acknowledged")(!formData["is_acknowledged"])
              }
              checked={Boolean(formData.is_acknowledged)}
              errorMessage={formErrors.is_acknowledged?.message}
              hasAttemptedFormSubmission={hasAttemptedFormSubmission}
            />
            <DisclaimerWithAcknowledgement
              changeHandler={updateField("disclaimer")}
              errorMessage={formErrors.disclaimer?.message}
              hasAttemptedFormSubmission={hasAttemptedFormSubmission}
              signature={formData.disclaimer}
            />
          </>
        )}
      </Col>
    </FormLayout>
  );
};
